/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019, 2020
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
	classnames as cx,
	arrayFilters,
	propsUtils,
	lodash,
	keyboardUtils,
} from '@ba-ui-toolkit/utils';
import Tags from '@ba-ui-toolkit/tags';
import ListDropdown from '@ba-ui-toolkit/listdropdown';
import close16 from '@ba-ui-toolkit/ba-graphics/dist/icons-js/close_16';
import styles from './TagInput.scss';

// Utils
const { containsFilter } = arrayFilters;

const { getKeyName, keyNames } = keyboardUtils;
const { sanitize, getInheritedProps } = propsUtils;
const { get, uniqueId } = lodash;
const tagInputClassName = styles.tagInputClassName.replace(/"/g, '');

function filterTags(options, selectedTags) {
	return options.filter(option => selectedTags.every(tag => tag.value !== option.value));
}

export default class TagInput extends Component {
	static propTypes = {
		...sanitize(ListDropdown.propTypes, null, 'triggerNodeRenderer', 'options'),
		/** Classname to be given to the container of TagInput */
		className: PropTypes.string,
		/** Value of the input field */
		value: PropTypes.string,
		/** Custom filtering function */
		customFilter: PropTypes.func,
		/** Custom function to control the open state of the dropdown */
		customOpenStateController: PropTypes.func,
		/** Width set */
		customWidth: PropTypes.string,
		/** Disable tag input */
		disabled: PropTypes.bool,
		/** To show validation error state */
		hasValidationError: PropTypes.bool,
		/** ID for input of TagInput */
		id: PropTypes.string,
		/** Function that is called when a tag is added and return the added tag */
		onTagAdded: PropTypes.func,
		/** Function that is called when value is typed into input field */
		onChange: PropTypes.func,
		/** Function that is called when a tag is removed and return the removed tag */
		onTagRemoved: PropTypes.func,
		/** Function that is called when a selected tag array is changed and return the selected tags array */
		onTagsChanged: PropTypes.func,
		/** Placeholder text */
		placeholder: PropTypes.string,
		/** Choose between one of the two fixed sizes */
		size: PropTypes.oneOf(['normal', 'small']),
		/** To show validation success message */
		successText: PropTypes.string,
		/** Selected tags to show in the input box, with designated colors for preset */
		selectedTags: PropTypes.array,
		/** Number key is for the maximum number of tags a user can select and onWarning is a callback function when a user tries to go over the limit. */
		selectionCap: PropTypes.object,
		/** Tag options where a user select tags from, , with designated colors for preset */
		tagOptions: PropTypes.array,
		/** To show validation error message */
		validationErrorText: PropTypes.string,
		/** To truncate the tag label (in middle) if it exceeds this max width (in px) */
		maxTagWidth: PropTypes.number,
	};

	static defaultProps = {
		className: '',
		customWidth: '100%',
		disabled: false,
		selectedTags: [],
		size: 'normal',
		tagOptions: [],
		value: '',
	};

	constructor(props) {
		super(props);

		const tagActionIcon = {
			id: close16.id,
			onClick: this._deleteTag.bind(this),
		};

		this.state = {
			availTags:
				typeof this.props.selectedTags !== 'undefined'
					? filterTags(this.props.tagOptions, this.props.selectedTags)
					: this.props.tagOptions,
			customWidth: this.props.customWidth,
			open: false,
			selectedTags: this.props.selectedTags,
			showFocusClass: false,
			tagActionIcon,
			tagOptions: this.props.tagOptions,
			warning: false,
			savedLabel: this.props.value,
		};

		this._id = uniqueId('__tagInput_');
		this._editLabel = this._editLabel.bind(this);
	}

	componentDidMount() {
		window.addEventListener('resize', this._close);
	}

	componentWillUnmount() {
		window.removeEventListener('resize', this._close);
	}

	componentDidUpdate(prevProps) {
		if (this.props.value !== prevProps.value) {
			// If the value prop changed, update the internal state.
			this._updateState(true, this.props.value);
		} else if (
			JSON.stringify(this.props.tagOptions) !== JSON.stringify(prevProps.tagOptions) ||
			JSON.stringify(this.props.selectedTags) !== JSON.stringify(prevProps.selectedTags)
		) {
			// This way of comparing JavaScript arrays is not ideal, but does the job here.
			// A more comprehensive and elegant way would be to *recursively* compare each element by value.
			this._updateState(true);
		}

		// Max height of tagInput should be 96px as designed.
		if (this._tagList && this._tagList._list.offsetHeight > 96) {
			this._tagList._node.scrollTop =
				this._tagList._node.scrollHeight - this._tagList._node.clientHeight;
		}
	}

	_onClose = () => {
		this.setState({ open: false });
	};

	blur() {
		this.inputNode && this.inputNode.blur();
	}

	focus() {
		!this.props.disabled && this.inputNode && this.inputNode.focus();
	}

	_refreshOptions(tagOptions, selectedTags) {
		return filterTags(tagOptions, selectedTags);
	}

	_onInputValChange = e => {
		const { onChange } = this.props;
		const value = e.target.value;
		const handled = onChange && onChange(value, e);
		if (!handled) {
			this._updateState(false, value);
		}
	};

	/*
	 * Called when tagOptions or selectedTags are updated or when input text changes
	 */
	_updateState = (propsUpdated, newInputText) => {
		const newState = {};
		if (propsUpdated) {
			newState.selectedTags = this.props.selectedTags;
			newState.tagOptions = this.props.tagOptions;
			newState.availTags =
				typeof this.props.selectedTags !== 'undefined'
					? filterTags(this.props.tagOptions, this.props.selectedTags)
					: this.props.tagOptions;
			newState.focusedInput = true;
		}

		const { open } = this.state;
		const inputText = newInputText !== undefined ? newInputText : this.inputNode.value;
		const { tagOptions, selectedTags, customFilter, customOpenStateController } = this.props;
		const freshOptionSet = this._refreshOptions(tagOptions, selectedTags);

		const searchResult = customFilter
			? customFilter({ value: inputText, values: freshOptionSet })
			: freshOptionSet.filter(containsFilter(inputText));

		if (customOpenStateController) {
			const customOpen = customOpenStateController({ value: inputText });
			if (customOpen !== open) {
				newState.open = customOpen;
			}
			if (customOpen) {
				newState.availTags = searchResult;
			}
		} else if (!inputText || searchResult.length < 1) {
			if (open) {
				newState.open = false;
			}
		} else if (searchResult.length > 0) {
			newState.availTags = searchResult;
			if (!open) {
				newState.open = true;
			}
		}
		newState.savedLabel = inputText;

		if (Object.entries(newState).length > 0) this.setState(newState);
	};

	_onInputKeyDown = e => {
		switch (getKeyName(e)) {
			case keyNames.UP:
			case keyNames.DOWN:
			case keyNames.ENTER:
				e.preventDefault();
				this._dropdown && this._dropdown._list && this._dropdown._list.handleKeyEvent(e);
				if (!this.state.open) {
					const { customOpenStateController } = this.props;
					const nextOpen = customOpenStateController
						? customOpenStateController({ event: e, value: this.inputNode.value })
						: true;
					if (nextOpen) {
						this.setState({
							availTags: filterTags(this.props.tagOptions, this.props.selectedTags),
							open: true,
						});
					}
				}
				break;
			case keyNames.ESCAPE:
				this._close();
				break;
			case keyNames.BACKSPACE:
				if (e.target.value === '') {
					const { onTagRemoved, onTagsChanged } = this.props;
					const backspaceDelTag = this.state.selectedTags.pop();
					this.setState({ selectedTags: this.state.selectedTags });
					onTagRemoved && onTagRemoved(backspaceDelTag);
					onTagsChanged && onTagsChanged(this.state.selectedTags);
				}
				break;
			default:
				break;
		}
	};

	_onBlur = e => {
		const listNode = get(this, '_dropdown._list._node');
		const dropdownWrapper = get(this, '_dropdown._wrapper');
		if (
			(listNode &&
				(listNode.contains(document.activeElement) ||
					listNode.contains(e.relatedTarget))) ||
			(dropdownWrapper && dropdownWrapper.contains(document.activeElement)) ||
			e.target !== e.currentTarget
		) {
			this.focus();
		} else {
			this.blur();
			this.setState({ focusedInput: false, open: false });
		}
	};

	_onFocus = () => {
		this.focus();
		this._setInputFocused();
	};

	_dropdownOnActiveChange = focusIndex => {
		this.setState({ focusIndex });
	};

	_setInputFocused = () => {
		this.setState({ focusedInput: true });
	};

	_close = () => {
		if (this.state.open) this.setState({ open: false });
	};

	_onTagSelect = (value, name, index) => {
		if (this.state.open) {
			const { selectionCap, onTagAdded, onTagsChanged } = this.props;
			const { availTags, selectedTags } = this.state;

			if (selectionCap && selectedTags.length >= selectionCap.number) {
				selectionCap.onWarning();
			} else {
				const newSelectedTags = this.state.selectedTags.concat([availTags[index]]);
				this.setState({
					selectedTags: newSelectedTags,
					availTags: filterTags(this.state.tagOptions, selectedTags),
					open: false,
					focusedInput: true,
					savedLabel: '',
				});
				onTagAdded && onTagAdded(availTags[index]);
				onTagsChanged && onTagsChanged(newSelectedTags);
				this.inputNode.value = '';
			}
		}
	};

	_deleteTag(e) {
		const { onTagRemoved, onTagsChanged } = this.props;
		const { tagOptions, selectedTags } = this.state;
		const id = e.target.attributes.tagvalue.value;
		const newSelectedTags = selectedTags.filter(tag => tag.value !== id);
		this.setState({
			selectedTags: newSelectedTags,
			availTags: filterTags(tagOptions, newSelectedTags),
		});
		onTagRemoved && onTagRemoved(selectedTags.find(tag => tag.value === id));
		onTagsChanged && onTagsChanged(newSelectedTags);
	}

	_editLabel = tag => {
		if (!this.props.disabled) {
			// delete the tag
			const { onTagRemoved, onTagsChanged } = this.props;
			const { tagOptions, selectedTags } = this.state;
			const newSelectedTags = selectedTags.filter(s => s.value !== tag.value);

			this.setState({
				selectedTags: newSelectedTags,
				availTags: filterTags(tagOptions, newSelectedTags),
				// set tag's label to be placed in input box
				savedLabel: tag.label,
			});

			onTagRemoved && onTagRemoved(selectedTags.find(s => s.value === tag.value));
			onTagsChanged && onTagsChanged(newSelectedTags);
		}
	};

	_triggerNodeRenderer = ref => {
		const {
			id,
			hasValidationError,
			validationErrorText,
			successText,
			placeholder,
			disabled,
			customWidth,
		} = this.props;
		const { showFocusClass, focusedInput, selectedTags, tagActionIcon, open } = this.state;

		const classes = cx(tagInputClassName, {
			disabled,
			error: hasValidationError,
			focus: showFocusClass,
			focusedInput,
			open,
			success: successText && !hasValidationError,
			tagsSelected: selectedTags.length > 0,
		});

		const inputProps = {
			autoComplete: 'off',
			'aria-labelledby': `${this._id}__required-field ${this._id}__error-message ${this._id}__success-message`,
			className: `${tagInputClassName}__input`,
			disabled,
			id,
			onBlur: this._onBlur,
			onChange: this._onInputValChange,
			onFocus: this._setInputFocused,
			onKeyDown: this._onInputKeyDown,
			placeholder: selectedTags.length === 0 ? placeholder : '',
			ref: this._inputRef,
			type: 'text',
			value: this.state.savedLabel,
		};

		return (
			<div className={classes} style={{ width: customWidth }} onClick={this._onFocus}>
				<div
					className="tag-input"
					ref={node => {
						this._node = node;
						if (node) ref(node);
					}}
				>
					<Tags
						data-tid="ba-common-taginput__tags"
						disabled={disabled}
						ref={this._tagListRef}
						tags={selectedTags}
						tagActionIcon={tagActionIcon}
						editLabel={this._editLabel}
						maxTagWidth={this.props.maxTagWidth}
					>
						<input {...inputProps} />
					</Tags>
					<hr className={`${tagInputClassName}__line`} />
				</div>
				{hasValidationError ? (
					<div className="validation-error" id={`${this._id}__error-message`}>
						{validationErrorText}
					</div>
				) : (
					<div className="success-message" id={`${this._id}__success-message`}>
						{successText}
					</div>
				)}
			</div>
		);
	};

	_inputRef = node => {
		this.inputNode = node;
	};
	_tagListRef = node => {
		this._tagList = node;
	};
	_dropdownRef = node => {
		this._dropdown = node;
	};

	render() {
		const { className, customWidth, size, ...others } = this.props;
		const { availTags, focusIndex, open } = this.state;
		const listDropdownPassthroughProps = getInheritedProps(others, ListDropdown);

		const dropdownProps = {
			...sanitize(others, TagInput),
			...listDropdownPassthroughProps,
			customWidth,
			focusIndex,
			onActiveChange: this._dropdownOnActiveChange,
			onChange: this._onTagSelect,
			onClose: this._onClose,
			onScrollClose: this._onClose,
			open,
			options: availTags,
			ref: this._dropdownRef,
			size,
			triggerNodeRenderer: this._triggerNodeRenderer,
			truncated: true,
		};

		return (
			<ListDropdown
				className={className}
				data-tid="ba-common-tag-input__dropdown"
				{...dropdownProps}
			/>
		);
	}
}
